import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { createVineyard, updateVineyard, getVineyard, uploadFile } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { ArrowLeft, Upload, X, Image as ImageIcon, Video, Plus } from 'lucide-react';

const VineyardForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated, canAddContent, loading: authLoading } = useAuth();
  const isEdit = !!id;

  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    region: '',
    country: '',
    producer: '',
    description: '',
    address: '',
    visiting_hours: '',
    phone: '',
    website: '',
    photos: [],
    videos: [],
    latitude: '',
    longitude: '',
  });

  useEffect(() => {
    if (authLoading) return;
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    if (!canAddContent) {
      toast.error('Your account type does not allow adding content');
      navigate('/vineyards');
      return;
    }
    if (isEdit) {
      loadVineyard();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, isAuthenticated, canAddContent, isEdit, navigate, authLoading]);

  const loadVineyard = async () => {
    try {
      const data = await getVineyard(id);
      setFormData({
        ...data,
        latitude: data.latitude || '',
        longitude: data.longitude || '',
      });
    } catch (error) {
      toast.error('Failed to load vineyard');
      navigate('/vineyards');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleFileUpload = async (e, type) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const urls = await Promise.all(files.map((file) => uploadFile(file)));
      setFormData((prev) => ({
        ...prev,
        [type]: [...prev[type], ...urls],
      }));
      toast.success(`${type === 'photos' ? 'Photo' : 'Video'}(s) uploaded`);
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const addMediaUrl = (type) => {
    const url = prompt(`Enter ${type === 'photos' ? 'image' : 'video'} URL:`);
    if (url) {
      setFormData((prev) => ({
        ...prev,
        [type]: [...prev[type], url],
      }));
    }
  };

  const removeMedia = (type, index) => {
    setFormData((prev) => ({
      ...prev,
      [type]: prev[type].filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.region || !formData.country || !formData.producer) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const submitData = {
        ...formData,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
      };

      if (isEdit) {
        await updateVineyard(id, submitData);
        toast.success('Vineyard updated');
      } else {
        const created = await createVineyard(submitData);
        toast.success('Vineyard created');
        navigate(`/vineyards/${created.id}`);
        return;
      }
      navigate(`/vineyards/${id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen py-8" data-testid="vineyard-form-page">
      <div className="container mx-auto px-4 md:px-8 max-w-3xl">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-stone-500 hover:text-stone-300 mb-8 transition-colors"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <h1 className="text-4xl font-serif text-stone-50 mb-8">
          {isEdit ? 'Edit Vineyard' : 'Add New Vineyard'}
        </h1>

        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Basic Info */}
          <div className="space-y-6 p-6 bg-stone-900/30 border border-white/5">
            <h2 className="text-xs uppercase tracking-widest text-[#d4af37]">Basic Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-stone-400">
                  Vineyard Name <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="bg-stone-900/50 border-stone-700"
                  placeholder="e.g., Château Margaux"
                  data-testid="name-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="producer" className="text-stone-400">
                  Producer / Winery <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="producer"
                  name="producer"
                  value={formData.producer}
                  onChange={handleChange}
                  required
                  className="bg-stone-900/50 border-stone-700"
                  placeholder="e.g., Grand Cru Classé"
                  data-testid="producer-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="region" className="text-stone-400">
                  Region <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="region"
                  name="region"
                  value={formData.region}
                  onChange={handleChange}
                  required
                  className="bg-stone-900/50 border-stone-700"
                  placeholder="e.g., Bordeaux, Médoc"
                  data-testid="region-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="country" className="text-stone-400">
                  Country <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="country"
                  name="country"
                  value={formData.country}
                  onChange={handleChange}
                  required
                  className="bg-stone-900/50 border-stone-700"
                  placeholder="e.g., France"
                  data-testid="country-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="description" className="text-stone-400">
                Description
              </Label>
              <Textarea
                id="description"
                name="description"
                value={formData.description}
                onChange={handleChange}
                rows={4}
                className="bg-stone-900/50 border-stone-700"
                placeholder="Describe the vineyard, its history, and what makes it special..."
                data-testid="description-input"
              />
            </div>
          </div>

          {/* Contact & Visit Info */}
          <div className="space-y-6 p-6 bg-stone-900/30 border border-white/5">
            <h2 className="text-xs uppercase tracking-widest text-[#d4af37]">Contact & Visit Information</h2>
            
            <div className="space-y-2">
              <Label htmlFor="address" className="text-stone-400">Address</Label>
              <Input
                id="address"
                name="address"
                value={formData.address}
                onChange={handleChange}
                className="bg-stone-900/50 border-stone-700"
                placeholder="Full address"
                data-testid="address-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="visiting_hours" className="text-stone-400">Visiting Hours</Label>
                <Input
                  id="visiting_hours"
                  name="visiting_hours"
                  value={formData.visiting_hours}
                  onChange={handleChange}
                  className="bg-stone-900/50 border-stone-700"
                  placeholder="e.g., Mon-Sat 10am-6pm"
                  data-testid="visiting-hours-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-stone-400">Phone</Label>
                <Input
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  className="bg-stone-900/50 border-stone-700"
                  placeholder="+1 234 567 8900"
                  data-testid="phone-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="website" className="text-stone-400">Website</Label>
              <Input
                id="website"
                name="website"
                value={formData.website}
                onChange={handleChange}
                className="bg-stone-900/50 border-stone-700"
                placeholder="https://www.example.com"
                data-testid="website-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="latitude" className="text-stone-400">Latitude</Label>
                <Input
                  id="latitude"
                  name="latitude"
                  type="number"
                  step="any"
                  value={formData.latitude}
                  onChange={handleChange}
                  className="bg-stone-900/50 border-stone-700"
                  placeholder="e.g., 45.1234"
                  data-testid="latitude-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="longitude" className="text-stone-400">Longitude</Label>
                <Input
                  id="longitude"
                  name="longitude"
                  type="number"
                  step="any"
                  value={formData.longitude}
                  onChange={handleChange}
                  className="bg-stone-900/50 border-stone-700"
                  placeholder="e.g., -0.5678"
                  data-testid="longitude-input"
                />
              </div>
            </div>
          </div>

          {/* Photos */}
          <div className="space-y-6 p-6 bg-stone-900/30 border border-white/5">
            <h2 className="text-xs uppercase tracking-widest text-[#d4af37] flex items-center gap-2">
              <ImageIcon className="w-4 h-4" /> Photos
            </h2>
            
            <div className="flex gap-4 flex-wrap">
              {formData.photos.map((photo, index) => (
                <div key={`photo-${photo.substring(0, 20)}-${index}`} className="relative w-24 h-24 group">
                  <img
                    src={photo}
                    alt={`Photo ${index + 1}`}
                    className="w-full h-full object-cover border border-stone-700"
                  />
                  <button
                    type="button"
                    onClick={() => removeMedia('photos', index)}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              ))}
              
              <label className="w-24 h-24 border-2 border-dashed border-stone-700 flex flex-col items-center justify-center cursor-pointer hover:border-[#d4af37] transition-colors">
                <Upload className="w-6 h-6 text-stone-600 mb-1" />
                <span className="text-xs text-stone-600">Upload</span>
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFileUpload(e, 'photos')}
                  disabled={uploading}
                  data-testid="photo-upload"
                />
              </label>

              <button
                type="button"
                onClick={() => addMediaUrl('photos')}
                className="w-24 h-24 border-2 border-dashed border-stone-700 flex flex-col items-center justify-center hover:border-[#d4af37] transition-colors"
                data-testid="add-photo-url-btn"
              >
                <Plus className="w-6 h-6 text-stone-600 mb-1" />
                <span className="text-xs text-stone-600">Add URL</span>
              </button>
            </div>
          </div>

          {/* Videos */}
          <div className="space-y-6 p-6 bg-stone-900/30 border border-white/5">
            <h2 className="text-xs uppercase tracking-widest text-[#d4af37] flex items-center gap-2">
              <Video className="w-4 h-4" /> Videos
            </h2>
            
            <div className="flex gap-4 flex-wrap">
              {formData.videos.map((video, index) => (
                <div key={`video-${video.substring(0, 20)}-${index}`} className="relative w-40 h-24 group">
                  <video
                    src={video}
                    className="w-full h-full object-cover border border-stone-700"
                  />
                  <button
                    type="button"
                    onClick={() => removeMedia('videos', index)}
                    className="absolute -top-2 -right-2 w-6 h-6 bg-red-600 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    <X className="w-4 h-4 text-white" />
                  </button>
                </div>
              ))}
              
              <label className="w-40 h-24 border-2 border-dashed border-stone-700 flex flex-col items-center justify-center cursor-pointer hover:border-[#d4af37] transition-colors">
                <Upload className="w-6 h-6 text-stone-600 mb-1" />
                <span className="text-xs text-stone-600">Upload Video</span>
                <input
                  type="file"
                  accept="video/*"
                  multiple
                  className="hidden"
                  onChange={(e) => handleFileUpload(e, 'videos')}
                  disabled={uploading}
                  data-testid="video-upload"
                />
              </label>

              <button
                type="button"
                onClick={() => addMediaUrl('videos')}
                className="w-40 h-24 border-2 border-dashed border-stone-700 flex flex-col items-center justify-center hover:border-[#d4af37] transition-colors"
                data-testid="add-video-url-btn"
              >
                <Plus className="w-6 h-6 text-stone-600 mb-1" />
                <span className="text-xs text-stone-600">Add URL</span>
              </button>
            </div>
          </div>

          {/* Submit */}
          <div className="flex gap-4">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate(-1)}
              className="flex-1 border-stone-700 rounded-none"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={loading || uploading}
              className="flex-1 bg-[#d4af37] text-stone-950 hover:bg-[#b5952f] rounded-none uppercase tracking-widest text-xs"
              data-testid="submit-btn"
            >
              {loading ? 'Saving...' : isEdit ? 'Update Vineyard' : 'Create Vineyard'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VineyardForm;
